import { motion } from 'framer-motion';
import { Check, X, AlertTriangle } from 'lucide-react';

const bringItems = [
  'Valid photo ID',
  'Your ticket (digital or printed)',
  'Tent and camping gear',
  'Sleeping bag and pillow',
  'Warm clothing for cool nights',
  'Rain jacket or poncho',
  'Torch/headlamp',
  'Sunscreen and hat',
  'Reusable water bottle',
  'Reusable cup for the bar',
  'Bank card (the festival is cashless)',
  'Toiletries and towel',
  'Ear plugs (for sleeping!)',
  'First aid basics',
  'Phone charger/power bank',
  'Snacks and non-perishable food',
  'Esky/cooler with ice',
  'Camp chairs and shade shelter',
  'Closed shoes for night time',
];

const dontBringItems = [
  'Alcohol & any alcoholic drinks (this is NOT a BYO festival)',
  'Glass of any kind (including mirrors)',
  'Illegal substances',
  'Fireworks or flares',
  'Weapons of any kind',
  'Drones',
  'Pets (except service animals)',
  'Sound systems or large speakers',
  'Generators',
  'Open fires, fire pits, or BBQs with open flame',
  'Single-use plastics where possible',
  'Spray paint or graffiti materials',
  'Unauthorized commercial goods',
  'Bad vibes',
];

export default function WhatToBring() {
  return (
    <div className="min-h-screen bg-ff-blue pt-20" data-testid="what-to-bring-page">
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="badge mb-4">
              Event Details
            </span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-ff-brown">
              What To <span className="text-ff-brown">Bring</span>
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* What to Bring */}
              <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-500" />
                  </div>
                  <h2 className="font-display text-2xl text-ff-brown">Bring</h2>
                </div>
                <ul className="space-y-2">
                  {bringItems.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-ff-brown/70 text-sm">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* What NOT to Bring */}
              <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <X className="w-5 h-5 text-red-500" />
                  </div>
                  <h2 className="font-display text-2xl text-ff-brown">Don't Bring</h2>
                </div>
                <ul className="space-y-2">
                  {dontBringItems.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-ff-brown/70 text-sm">
                      <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Important Notice */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 border-l-4 border-yellow-500">
              <div className="flex items-center gap-4 mb-4">
                <AlertTriangle className="w-6 h-6 text-yellow-500" />
                <h3 className="font-display text-xl text-ff-brown">Important Notice</h3>
              </div>
              <p className="text-ff-brown/70 mb-4">
                All vehicles and bags will be checked upon entry. Prohibited items will be 
                confiscated and may result in denied entry without refund. We reserve the 
                right to refuse entry or remove any person from the event.
              </p>
              <p className="text-ff-brown/70 text-sm">
                <strong className="text-ff-brown">NO GLASS</strong> – This is strictly 
                enforced for safety reasons. Decant drinks into plastic containers before arrival.
              </p>
            </div>

            {/* Pro Tips */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 mt-8">
              <h3 className="font-display text-xl text-ff-brown mb-4">Festival Pro Tips</h3>
              <ul className="text-ff-brown/70 space-y-2 text-sm">
                <li>• Bring more water than you think you need</li>
                <li>• A wagon or trolley helps transport gear from car to campsite</li>
                <li>• Set up camp before dark – bring a headlamp!</li>
                <li>• Mark your campsite so you can find it in the dark</li>
                <li>• Bring a shade structure – September can still be sunny</li>
                <li>• Prepare for all weather conditions – pack for sun, rain and cool nights</li>
                <li>• Baby wipes are your best friend</li>
                <li>• A small lock for your tent can provide peace of mind</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
